import { useMemo } from 'react'
import { Calendar, dayjsLocalizer, type View } from 'react-big-calendar'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import { alpha, useTheme } from '@mui/material/styles'
import { formatTime } from '@jupi/ui'
import type { Booking } from '../../api/queries'

const localizer = dayjsLocalizer(dayjs)

// 24-hour clock with a dot, as everywhere else in the app (13.00).
const hm = (date: Date | string) => dayjs(date).format('HH.mm')
const range = ({ start, end }: { start: Date; end: Date }) => `${hm(start)} – ${hm(end)}`
const formats = {
  timeGutterFormat: hm,
  eventTimeRangeFormat: range,
  selectRangeFormat: range,
  agendaTimeFormat: hm,
  agendaTimeRangeFormat: range,
}

interface LessonEvent {
  title: string
  start: Date
  end: Date
  booking?: Booking
  // Offered time drawn as an event, where there is no time axis to shade.
  available?: boolean
}

// An instructor's lessons, with the time they have offered shaded behind
// them. The availability shows in the day and week views, where there is a
// time axis to shade.
export function LessonsCalendar({
  view,
  date,
  lessons,
  availability,
  onView,
  onNavigate,
  onSelectLesson,
  onSelectSlot,
}: {
  view: View
  date: Date
  lessons: Booking[]
  availability: { startsAt: string; endsAt: string }[]
  onView: (view: View) => void
  onNavigate: (date: Date) => void
  onSelectLesson: (booking: Booking) => void
  onSelectSlot: (start: Date, end: Date) => void
}) {
  const { t, i18n } = useTranslation()
  const theme = useTheme()

  // The month and agenda views have no time axis to shade, so the offered
  // time is listed beside the lessons there.
  const timeAxis = view === 'week' || view === 'day'
  const offered = useMemo<LessonEvent[]>(
    () =>
      availability.map((a) => ({
        title: t('lessons.available'),
        start: new Date(a.startsAt),
        end: new Date(a.endsAt),
        available: true,
      })),
    [availability, t],
  )
  const events = useMemo<LessonEvent[]>(
    () => [
      ...lessons.map((booking) => ({
        title: [booking.displayName, booking.lessonNumber, booking.registration].filter(Boolean).join(' · '),
        start: new Date(booking.startsAt),
        end: new Date(booking.endsAt),
        booking,
      })),
      ...(timeAxis ? [] : offered),
    ],
    [lessons, offered, timeAxis],
  )
  const background = timeAxis ? offered : []

  const messages = useMemo(
    () => ({
      today: t('common.today'),
      previous: t('calendar.previous'),
      next: t('calendar.next'),
      month: t('calendar.month'),
      week: t('calendar.week'),
      day: t('calendar.day'),
      agenda: t('calendar.agenda'),
      noEventsInRange: t('lessons.noLessons'),
      allDay: '',
      showMore: (count: number) => `+${count}`,
    }),
    [t],
  )

  return (
    <Box
      sx={{
        height: { xs: 520, md: 680 },
        '& .rbc-calendar': { fontFamily: theme.typography.fontFamily },
        '& .rbc-background-event': {
          backgroundColor: alpha(theme.palette.success.main, 0.18),
          border: `1px solid ${alpha(theme.palette.success.main, 0.5)}`,
          color: theme.palette.text.secondary,
          fontSize: 11,
        },
      }}
    >
      <Calendar<LessonEvent>
        localizer={localizer}
        formats={formats}
        culture={i18n.language}
        events={events}
        backgroundEvents={background}
        view={view}
        date={date}
        onView={onView}
        onNavigate={onNavigate}
        views={['month', 'week', 'day', 'agenda']}
        messages={messages}
        popup
        selectable
        step={30}
        timeslots={2}
        scrollToTime={new Date(new Date(date).setHours(7, 0, 0, 0))}
        dayLayoutAlgorithm="overlap"
        onSelectEvent={(event) => event.booking && onSelectLesson(event.booking)}
        onSelectSlot={(slot) => onSelectSlot(slot.start as Date, slot.end as Date)}
        eventPropGetter={(event) => ({
          style: event.available
            ? {
                backgroundColor: alpha(theme.palette.success.main, 0.18),
                color: theme.palette.success.dark,
                border: `1px solid ${alpha(theme.palette.success.main, 0.5)}`,
                borderRadius: 6,
                fontSize: 12,
              }
            : {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: 6,
                fontSize: 12,
              },
        })}
        tooltipAccessor={(event) =>
          event.booking || event.available ? `${formatTime(event.start)}–${formatTime(event.end)} ${event.title}` : ''
        }
      />
    </Box>
  )
}
